import React, { useEffect, useRef } from 'react';

// ─── Config ─────────────────────────────────────────────────────────────────
// Toggle click-to-push via .env → REACT_APP_PARTICLES_CLICK_PUSH=true/false
const CLICK_PUSH_ENABLED = process.env.REACT_APP_PARTICLES_CLICK_PUSH === 'true';

const CONFIG = {
  particles: {
    number:      { value: 60, density: { enable: true, value_area: 800 } },
    color:       '#ffffff',
    opacity:     0.5,
    size:        { value: 3, random: true },
    line_linked: { enable: true, distance: 120, color: '#ffffff', opacity: 0.35, width: 1 },
    move:        { speed: 2 }
  },
  interactivity: {
    grab:  { distance: 140, line_opacity: 1 },
    push:  { particles_nb: 4 }
  },
  retina_detect: true
};
// ─────────────────────────────────────────────────────────────────────────────

const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas  = canvasRef.current;
    const ctx     = canvas.getContext('2d');
    const pxRatio = CONFIG.retina_detect ? (window.devicePixelRatio || 1) : 1;

    let particles = [];
    let raf;
    let W, H, canvasRect;

    // Mouse coords relative to canvas; null = outside canvas
    let mouseX = null, mouseY = null;

    // ── Sizing ──────────────────────────────────────────────────────────────
    function setSize() {
      W = canvas.offsetWidth  || window.innerWidth;
      H = canvas.offsetHeight || window.innerHeight;
      canvas.width  = W * pxRatio;
      canvas.height = H * pxRatio;
      ctx.setTransform(pxRatio, 0, 0, pxRatio, 0, 0);
      canvasRect = canvas.getBoundingClientRect();
    }

    // ── Particle factory ────────────────────────────────────────────────────
    function makeParticle(x, y) {
      const speed = CONFIG.particles.move.speed;
      const size  = CONFIG.particles.size.random
        ? Math.random() * CONFIG.particles.size.value + 0.5
        : CONFIG.particles.size.value;
      return {
        x:  x !== undefined ? x : Math.random() * W,
        y:  y !== undefined ? y : Math.random() * H,
        vx: (Math.random() - 0.5) * speed * 0.4,
        vy: (Math.random() - 0.5) * speed * 0.4,
        size
      };
    }

    // ── Init ────────────────────────────────────────────────────────────────
    function init() {
      particles = [];
      let count = CONFIG.particles.number.value;
      if (CONFIG.particles.number.density.enable) {
        count = Math.round(count * (W * H) / CONFIG.particles.number.density.value_area / 400);
      }
      for (let i = 0; i < count; i++) particles.push(makeParticle());
    }

    // ── Draw helpers ────────────────────────────────────────────────────────
    function drawParticle(p) {
      ctx.save();
      ctx.globalAlpha = CONFIG.particles.opacity;
      ctx.fillStyle   = CONFIG.particles.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    function drawLinks() {
      const ll     = CONFIG.particles.line_linked;
      const distSq = ll.distance * ll.distance;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d2 = dx * dx + dy * dy;
          if (d2 < distSq) {
            ctx.save();
            ctx.strokeStyle = ll.color;
            ctx.globalAlpha = ll.opacity * (1 - d2 / distSq);
            ctx.lineWidth   = ll.width;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
            ctx.restore();
          }
        }
      }
    }

    // Grab lines only drawn when mouseX/mouseY is non-null (cursor inside canvas)
    function drawGrab() {
      if (mouseX === null) return;
      const { distance, line_opacity } = CONFIG.interactivity.grab;
      const ll     = CONFIG.particles.line_linked;
      const distSq = distance * distance;

      for (const p of particles) {
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const d2 = dx * dx + dy * dy;
        if (d2 < distSq) {
          const alpha = line_opacity * (1 - Math.sqrt(d2) / distance);
          ctx.save();
          ctx.strokeStyle = ll.color;
          ctx.globalAlpha = alpha;
          ctx.lineWidth   = ll.width;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mouseX, mouseY);
          ctx.stroke();
          ctx.restore();
        }
      }
    }

    function moveParticle(p) {
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < -p.size)    p.x = W + p.size;
      if (p.x > W + p.size) p.x = -p.size;
      if (p.y < -p.size)    p.y = H + p.size;
      if (p.y > H + p.size) p.y = -p.size;
    }

    // ── Render ──────────────────────────────────────────────────────────────
    function render() {
      ctx.clearRect(0, 0, W, H);
      for (const p of particles) { moveParticle(p); drawParticle(p); }
      drawLinks();
      drawGrab();
      raf = requestAnimationFrame(render);
    }

    // ── Events ──────────────────────────────────────────────────────────────
    // Listen on WINDOW so overlapping elements don't swallow the event.
    // Calculate canvas-relative position; null out when outside canvas.
    const onMouseMove = (e) => {
      canvasRect = canvas.getBoundingClientRect(); // refresh on every move (handles scroll/resize)
      const rx = e.clientX - canvasRect.left;
      const ry = e.clientY - canvasRect.top;
      if (rx >= 0 && rx <= canvasRect.width && ry >= 0 && ry <= canvasRect.height) {
        mouseX = rx;
        mouseY = ry;
      } else {
        mouseX = null;
        mouseY = null;
      }
    };

    const onMouseLeave = () => { mouseX = null; mouseY = null; };

    const onClick = (e) => {
      if (!CLICK_PUSH_ENABLED) return;
      canvasRect = canvas.getBoundingClientRect();
      const rx = e.clientX - canvasRect.left;
      const ry = e.clientY - canvasRect.top;
      // Only push if click was inside the canvas region
      if (rx < 0 || rx > canvasRect.width || ry < 0 || ry > canvasRect.height) return;
      for (let i = 0; i < CONFIG.interactivity.push.particles_nb; i++) {
        particles.push(makeParticle(rx, ry));
      }
    };

    const onResize = () => { setSize(); init(); };

    // ── Boot ─────────────────────────────────────────────────────────────────
    setSize();
    init();
    render();

    window.addEventListener('mousemove',  onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('click',      onClick);
    window.addEventListener('resize',     onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove',  onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('click',      onClick);
      window.removeEventListener('resize',     onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:       'absolute',
        top:            0,
        left:           0,
        width:          '100%',
        height:         '100%',
        background:     '#000000',
        zIndex:         0,
        display:        'block',
        pointerEvents:  'none'   // let clicks/hovers pass through to page content
      }}
    />
  );
};

export default ParticleBackground;
